import { create } from 'zustand';
import type { UserPublic } from '@ccc/shared-types';

interface AuthState {
  user: UserPublic | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  fetchMe: () => Promise<void>;
  register: (input: { username: string; password: string }) => Promise<boolean>;
  login: (input: { username: string; password: string; adminPassphrase?: string }) => Promise<'ok' | 'admin_passphrase_required' | 'error'>;
  bootstrapOwner: (input: { bootstrapToken: string; password: string; adminPassphrase: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  rotateSupportCode: () => Promise<string | null>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  initialized: false,
  error: null,
  async fetchMe() {
    set({ loading: true });
    try {
      const r = await fetch('/api/auth/me');
      const data = (await r.json()) as { user: UserPublic | null };
      set({ user: data.user, initialized: true, loading: false, error: null });
    } catch {
      set({ user: null, initialized: true, loading: false, error: 'Could not reach the server.' });
    }
  },
  async register(input) {
    set({ loading: true, error: null });
    const r = await jsonPost('/api/auth/register', input);
    if (!r.ok) {
      set({ loading: false, error: messageFrom(r.data, 'Could not create that account.') });
      return false;
    }
    set({ user: (r.data as { user: UserPublic }).user, loading: false, initialized: true });
    return true;
  },
  async login(input) {
    set({ loading: true, error: null });
    const r = await jsonPost('/api/auth/login', input);
    if (!r.ok) {
      const data = r.data as { error?: string; message?: string };
      set({ loading: false, error: data.error === 'admin_passphrase_required' ? null : messageFrom(data, 'Could not sign in.') });
      return data.error === 'admin_passphrase_required' ? 'admin_passphrase_required' : 'error';
    }
    set({ user: (r.data as { user: UserPublic }).user, loading: false, initialized: true });
    return 'ok';
  },
  async bootstrapOwner(input) {
    set({ loading: true, error: null });
    const r = await jsonPost('/api/auth/bootstrap-owner', input);
    if (!r.ok) {
      set({ loading: false, error: messageFrom(r.data, 'Could not create the owner account.') });
      return false;
    }
    set({ user: (r.data as { user: UserPublic }).user, loading: false, initialized: true });
    return true;
  },
  async logout() {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    set({ user: null, initialized: true, error: null });
  },
  async rotateSupportCode() {
    const r = await jsonPost('/api/support/code/rotate', {});
    if (!r.ok) {
      set({ error: messageFrom(r.data, 'Could not make a support code.') });
      return null;
    }
    await get().fetchMe();
    return (r.data as { code: string }).code;
  },
}));

async function jsonPost(path: string, body: unknown): Promise<{ ok: boolean; data: unknown }> {
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, data };
}

function messageFrom(data: unknown, fallback: string): string {
  if (data && typeof data === 'object') {
    const obj = data as { message?: unknown; error?: unknown };
    if (typeof obj.message === 'string' && obj.message.length > 0) return obj.message;
    if (typeof obj.error === 'string' && obj.error.length > 0) return `${fallback} (${obj.error})`;
  }
  return fallback;
}
